import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicesStatusBadge } from '../../InvoicesStatusBadge/InvoicesStatusBadge';
import Icon from '../../../../../../generic-components/Icon';
import { invoicePageIcon } from '../../../../../../assets/svg';
import { convertValueToUSD } from '../../../../../../helpers/currency.helper';
import { formatDate, getNumOfOverdueDays, isUnsentOverdueInvoice } from '../../../utils';
import { InvoicePageActions } from './InvoicePageActions';
import { DueDateNotice } from '../../DueDateNotice';
import { featureAccess, LimitedFeatureType } from '../../../../../../services/appAccess.service';
import { FEATURE_FLAG } from '../../../../../../constants/general';
import { FeatureAccess } from '../../../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

interface InvoiceSummaryProps {
  invoiceNumber: number;
  recipient: string;
  grossAmount: number;
  dueDate: string;
  status: string;
  paidTime?: string;
}

export const InvoiceSummary = ({
  invoiceNumber,
  recipient,
  grossAmount,
  dueDate,
  status,
  paidTime,
}: InvoiceSummaryProps) => {
  const { t } = useTranslation();
  const invoicePageTitle = `${t('INVOICE_HUB.INVOICE_PAGE.SUMMARY.TITLE')} #${invoiceNumber}`;
  const invoiceRecipientTitle = `${t('INVOICE_HUB.INVOICE_PAGE.SUMMARY.INVOICE_TO')}`;
  const invoiceAmountText = useMemo(() => convertValueToUSD(grossAmount, 2), [grossAmount]);
  const invoiceDueDateText = `${t('INVOICE_HUB.INVOICE_PAGE.SUMMARY.DUE')} ${formatDate(dueDate, true)}`;
  const isInvoiceOverdue = isUnsentOverdueInvoice(status, getNumOfOverdueDays(dueDate));
  const invoicesPermission = featureAccess(FEATURE_FLAG.INVOICE, LimitedFeatureType.Invoices);

  return (
    <div className='flex min-[222px] basis-full justify-start flex-col bg-bw00 border-b-[1px] border-bw03'>
      <div className='h-[48px] flex flex-wrap flex-row border-b-[1px] border-bw03 justify-center items-center'>
        <Icon className='w-[20px] h-[20px] mr-[5px]' src={invoicePageIcon} />
        <div className='text-lg text-bw08'>{invoicePageTitle}</div>
      </div>
      <div className='px-[24px] pt-[12px] pb-[22px] flex flex-col'>
        <div className='pb-[12px] border-b-[1px] border-bw03 text-bw07 font-metricR text-sm'>
          {invoiceRecipientTitle}
          <span className='text-bw08 font-metricRMedium ml-[5px] inline-block'>{recipient}</span>
        </div>
        <div className='relative mt-[12px]'>
          <div className={`text-lg text-bw08 font-metricRSemibold leading-7 ${isInvoiceOverdue && 'text-red02'}`}>
            {invoiceAmountText}
          </div>
          {status && (
            <div className='absolute top-[4px] right-0'>
              <InvoicesStatusBadge status={status} />
            </div>
          )}
        </div>
        <div className='flex items-center'>
          <div className='text-sm text-bw08 font-metricR leading-5 mr-[4px]'>{invoiceDueDateText}</div>
          <DueDateNotice status={status} dueDate={dueDate} paidTime={paidTime} />
        </div>
        {invoicesPermission === FeatureAccess.READ_WRITE && <InvoicePageActions />}
      </div>
    </div>
  );
};
